import React, { useCallback, useContext, useMemo } from 'react'
import { Chip, ScreenContext } from 'react-components'
import { useTranslation } from '../../../../hooks/locale'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from '../../../../redux/types'
import { filterClick, getCurrentOpenedFilterModal, setCurrentOpenedFilterModal } from '../../../../redux/filters'
import { FilterModalType } from '../../filter-sort-section/types'
import { FilterSectionTransport } from '../../../../transports/search-result-listing-static-transport'

import SortModal from '../sort-modal'
import SortContent from '../../filter-sort-section/sort-section/sort-content'
import { getRelativeURL } from '../../../../utils/encoding-utils'
import { getTruncatedTextByChars } from '../../../../utils/miscellaneous-utils'
import { useRequestUserConfigContext } from '../../../../hooks/request'
import { useOnClickOutside } from '../../../../hooks/dom'
import { Container, DesktopTabletContainer, SortIcon, StyledChevron } from './styles'
import { useActiveSortOption } from '../filter-header/hooks'
import { stopPropagation } from '../../../../utils/dom-utils'

interface Props {
    filterStaticData: FilterSectionTransport
}

const SortPill: React.FC<Props> = ({ filterStaticData }) => {
    const dispatch = useDispatch<Dispatch>()
    const { isMobile, isTablet } = useContext(ScreenContext)
    const { t } = useTranslation()
    const user = useRequestUserConfigContext()
    const currentOpenedFilterModal = useSelector(getCurrentOpenedFilterModal)
    const { activeSortOption, isNonDefaultSortActive } = useActiveSortOption()

    const isSortOpen = currentOpenedFilterModal === FilterModalType.SORT
    const isMobileOrTablet = isMobile || isTablet

    const handleModalToggle = useCallback(() => {
        dispatch(
            setCurrentOpenedFilterModal(
                currentOpenedFilterModal === FilterModalType.NONE ? FilterModalType.SORT : FilterModalType.NONE,
            ),
        )
    }, [currentOpenedFilterModal, dispatch])

    const handleSortSubmit = useCallback(
        (link: string) => {
            const encodedLink = getRelativeURL(link)
            dispatch(filterClick(encodedLink, true, user))
            dispatch(setCurrentOpenedFilterModal(FilterModalType.NONE))
        },
        [dispatch, user],
    )

    const closeDropdown = useCallback(() => {
        if (isSortOpen && !isMobileOrTablet) {
            dispatch(setCurrentOpenedFilterModal(FilterModalType.NONE))
        }
    }, [isSortOpen, isMobileOrTablet, dispatch])

    const { ref: containerRef } = useOnClickOutside<HTMLDivElement>(closeDropdown)

    const activeSortLabelKey = activeSortOption ? `${t(activeSortOption.label)}` : ''

    const sortPillLabel = useMemo(() => {
        const label = isNonDefaultSortActive
            ? `${t('mobileFilter.sort')}: ${activeSortLabelKey}`.trim()
            : t('mobileFilter.sort')

        return getTruncatedTextByChars(label, 20)
    }, [isNonDefaultSortActive, activeSortLabelKey, t])

    return (
        <Container ref={containerRef} onMouseDown={stopPropagation}>
            <Chip
                onClick={handleModalToggle}
                label={sortPillLabel}
                selected={isNonDefaultSortActive}
                icon={isMobileOrTablet ? <SortIcon /> : <StyledChevron isOpen={isSortOpen} />}
            />
            {!isMobileOrTablet && (
                <DesktopTabletContainer isOpen={isSortOpen}>
                    <SortContent
                        className="sort-content"
                        handleSortSubmit={handleSortSubmit}
                        isModal
                        isSortOpen={isSortOpen}
                    />
                </DesktopTabletContainer>
            )}
            {isMobileOrTablet && (
                <SortModal
                    handleModalToggle={handleModalToggle}
                    handleSortSubmit={handleSortSubmit}
                    filterStaticData={filterStaticData}
                />
            )}
        </Container>
    )
}

export default SortPill
